import React from 'react';
import { useAuth } from '@/lib/auth';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldOff, LogOut } from 'lucide-react';
import { useUserRole } from '@/hooks/useUserRole';
import { LoadingSpinner } from '@/components/LoadingSpinner';

interface AdminGuardedRouteProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

export const AdminGuardedRoute: React.FC<AdminGuardedRouteProps> = ({
  children
}) => {
  const { user, signOut } = useAuth();
  const { data: roleData, isLoading: roleLoading } = useUserRole();

  // Role-based access: admin, content_manager, or partner can access admin panel
  const hasAccess = roleData?.canEditCourses ?? false;

  // Fallback to legacy is_admin check while role data loads or if role column doesn't exist yet
  const legacyAdmin = user?.user_metadata?.is_admin === true;
  const isAuthorized = hasAccess || legacyAdmin;

  const handleLogout = async () => {
    await signOut();
  };

  // First use ProtectedRoute to ensure user is authenticated
  return (
    <ProtectedRoute>
      {/* Show skeleton while role data is being fetched — preserves page structure */}
      {roleLoading ? (
        <div className="flex flex-col h-screen bg-gray-50 dark:bg-gray-900">
          <div className="h-16 flex-shrink-0 border-b border-gray-200 dark:border-gray-700 flex items-center px-4 gap-3">
            <div className="h-8 w-8 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
            <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700 animate-pulse" />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <LoadingSpinner className="h-8 w-8" />
          </div>
        </div>
      ) : isAuthorized ? (
        <>{children}</>
      ) : (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                <ShieldOff className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-2xl font-bold">Access Denied</CardTitle>
              <CardDescription className="mt-2">
                You don&apos;t have permission to access this admin area.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {roleData?.isPartner
                  ? 'Your partner account does not have access to this section. Please contact your account manager for assistance.'
                  : 'This area is restricted to authorized staff only. If you believe you should have access, please contact your system administrator.'}
              </p>
              <div className="pt-4">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Logged in as: {user?.email}
                  {roleData?.role && roleData.role !== 'user' && (
                    <span className="ml-1">({roleData.role})</span>
                  )}
                </p>
                <div className="space-y-2">
                  <Button
                    onClick={() => window.location.href = 'https://jdsimplified.com'}
                    className="w-full"
                  >
                    Go to JD Simplified
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="w-full"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </ProtectedRoute>
  );
};
